import React, { useState } from 'react';
import * as mui from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import NormalTextField from '../../../common/NormalTextField';
import { SectionHeader } from './UIComponents';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import NetworkService from '../../../../services/NetworkService';
import AssessmentIcon from '@mui/icons-material/Assessment';

const JobScheduling = ({ 
  jobs, 
  domains, 
  fetchJobs, 
  showNotification,
  changeView 
}) => {
  const [newJob, setNewJob] = useState({
    JOB_NAME: '',
    DOMAIN_ID: '',
    SCHEDULE_TYPE: 'daily',
    SCHEDULE_TIME: new Date(),
    DAYS_OF_WEEK: [],
    ENABLED: true,
    JOB_TYPE: 'access_termination' // Default job type
  });
  const [showJobForm, setShowJobForm] = useState(false);
  const [editJobId, setEditJobId] = useState(null);

  // Define job types
  const jobTypes = [
    { 
      value: 'access_termination', 
      label: 'Access Termination Job',
      description: 'Automatically terminate network access for terminated users'
    },
    { 
      value: 'admin_group_scan', 
      label: 'Admin Group Scan',
      description: 'Extract all administrative groups and privileged users in Active Directory'
    },
    { 
      value: 'terminated_user_scan', 
      label: 'Terminated User Scan',
      description: 'Extract all terminated users and their last login date'
    },
    { 
      value: 'patch_scan', 
      label: 'Patch Analysis',
      description: 'Extract all patches installed in the Active Directory during the period'
    }
  ];

  const handleJobChange = (e) => {
    const { name, value } = e.target;
    setNewJob({ ...newJob, [name]: value });
  };

  const handleScheduleTimeChange = (newTime) => {
    // Make sure we have a valid Date object
    if (newTime instanceof Date && !isNaN(newTime)) {
      setNewJob({ ...newJob, SCHEDULE_TIME: newTime });
    } else {
      console.warn('Invalid time format received:', newTime);
    }
  };

  const handleDaysOfWeekChange = (e) => {
    const { value } = e.target;
    setNewJob({ ...newJob, DAYS_OF_WEEK: typeof value === 'string' ? value.split(',') : value });
  };

  const addJob = async (e) => {
    e.preventDefault();
    
    // Check if DOMAIN_ID is valid
    if (!newJob.DOMAIN_ID) {
      showNotification('Error', 'Please select a domain for this job');
      return;
    }
    
    try {
      // Format the time to HH:MM:SS format for Django API
      const formattedJob = {
        ...newJob,
        SCHEDULE_TIME: newJob.SCHEDULE_TIME instanceof Date 
          ? newJob.SCHEDULE_TIME.toTimeString().split(' ')[0].substring(0, 8) // Format as HH:MM:SS
          : newJob.SCHEDULE_TIME
      };
      
      if (editJobId) {
        const response = await NetworkService.updateNetworkJob(editJobId, formattedJob);
        if (response) {
          showNotification('Successful', 'Successfully updated job schedule');
          await fetchJobs();
          resetJobForm();
        }
      } else {
        const response = await NetworkService.createNetworkJob(formattedJob);
        if (response) {
          showNotification('Successful', 'Successfully added new job schedule');
          await fetchJobs();
          resetJobForm();
        }
      }
    } catch (error) {
      console.error('Error adding/updating job:', error);
      showNotification('Error', error.message || 'An error occurred');
    }
  };

  const editJob = (job) => {
    // Check if domains are available
    if (!domains || domains.length === 0) {
      showNotification('Error', 'No domains available. Please add a domain first before managing jobs.');
      return;
    }
    
    // If SCHEDULE_TIME is a string, convert it to a Date object
    let scheduleTime = job.SCHEDULE_TIME;
    if (typeof scheduleTime === 'string') {
      // Create a base date and set the time from the time string
      const today = new Date();
      const timeParts = scheduleTime.split(':');
      
      if (timeParts.length >= 2) {
        today.setHours(parseInt(timeParts[0], 10));
        today.setMinutes(parseInt(timeParts[1], 10));
        today.setSeconds(timeParts.length > 2 ? parseInt(timeParts[2], 10) : 0);
        scheduleTime = today;
      }
    }
    
    setNewJob({
      JOB_NAME: job.JOB_NAME,
      DOMAIN_ID: job.DOMAIN_ID,
      SCHEDULE_TYPE: job.SCHEDULE_TYPE,
      SCHEDULE_TIME: scheduleTime,
      DAYS_OF_WEEK: job.DAYS_OF_WEEK || [],
      ENABLED: job.ENABLED,
      JOB_TYPE: job.JOB_TYPE || 'access_termination' // Default to access_termination for backward compatibility
    });
    setEditJobId(job.id);
    setShowJobForm(true);
  };

  const deleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await NetworkService.deleteNetworkJob(jobId);
        showNotification('Successful', 'Successfully deleted job schedule');
        await fetchJobs();
      } catch (error) {
        console.error('Error deleting job:', error);
        showNotification('Error', error.message || 'An error occurred');
      }
    }
  };

  const resetJobForm = () => {
    setNewJob({
      JOB_NAME: '',
      DOMAIN_ID: '',
      SCHEDULE_TYPE: 'daily',
      SCHEDULE_TIME: new Date(),
      DAYS_OF_WEEK: [],
      ENABLED: true,
      JOB_TYPE: 'access_termination'
    });
    setEditJobId(null);
    setShowJobForm(false);
  };

  const toggleJobStatus = async (job) => {
    // Create updated job object with toggled status
    const updatedJob = {
      ...job,
      ENABLED: !job.ENABLED
    };
    
    try {
      const response = await NetworkService.updateNetworkJob(job.id, updatedJob);
      if (response) {
        showNotification('Success', `Job "${job.JOB_NAME}" ${!job.ENABLED ? 'enabled' : 'disabled'} successfully`);
        await fetchJobs(); // Refresh the job list
      }
    } catch (error) {
      console.error('Error toggling job status:', error);
      showNotification('Error', error.message || `Failed to ${!job.ENABLED ? 'enable' : 'disable'} job`);
    }
  };

  const bulkUpdateJobStatus = async (enableStatus) => {
    try {
      // Show a confirmation dialog before proceeding
      const confirmMessage = enableStatus 
        ? 'Are you sure you want to enable all jobs?' 
        : 'Are you sure you want to pause all jobs?';
          
      if (!window.confirm(confirmMessage)) {
        return;
      }
      
      // Get list of jobs that need their status changed
      const jobsToUpdate = jobs.filter(job => job.ENABLED !== enableStatus);
      
      if (jobsToUpdate.length === 0) {
        showNotification('No Action Taken', 'No jobs needed to be updated');
        return;
      }
      
      // Show loading indicator or message
      showNotification('Processing', `${enableStatus ? 'Enabling' : 'Pausing'} all jobs...`);
      
      // Update each job in sequence
      const results = await Promise.all(
        jobsToUpdate.map(job => 
          NetworkService.updateNetworkJob(job.id, { ...job, ENABLED: enableStatus })
        )
      );
      
      // Check results and show completion message
      const successCount = results.filter(Boolean).length;
      
      showNotification('Operation Complete', `Successfully ${enableStatus ? 'enabled' : 'paused'} ${successCount} of ${jobsToUpdate.length} jobs`);
      
      // Refresh the job list
      await fetchJobs();
      
    } catch (error) {
      console.error(`Error ${enableStatus ? 'enabling' : 'pausing'} all jobs:`, error);
      showNotification('Error', error.message || `Failed to ${enableStatus ? 'enable' : 'pause'} jobs`);
    }
  };

  const viewJobResults = (jobId, jobType) => {
    // Navigate to job results view with the specific job selected
    changeView('results', 'Job Results Analysis', { selectedJobId: jobId, selectedTab: jobType });
  };

  // Function to run a job immediately
  const runJob = async (job) => {
    // Show confirmation dialog
    if (!window.confirm(`Are you sure you want to run "${job.JOB_NAME}" right now?`)) {
      return;
    }
    
    try {
      showNotification('Processing', `Running job "${job.JOB_NAME}" now...`);
      
      const response = await NetworkService.runNetworkJob(job.id);
      
      if (response && response.success) {
        showNotification('Success', `Job "${job.JOB_NAME}" triggered successfully. Check job logs for results.`);
        
        // If we have results data in the response, show them directly
        if (response.resultsData && Array.isArray(response.resultsData) && response.resultsData.length > 0) {
          // Navigate to the job results view with the data
          changeView('results', 'Job Results Analysis', { 
            selectedJobId: job.id, 
            selectedTab: job.JOB_TYPE,
            initialResults: response.resultsData
          });
        }
        
        await fetchJobs(); // Refresh the job list to show updated run date
      } else {
        showNotification('Error', response.message || 'Failed to trigger job execution');
      }
    } catch (error) {
      console.error('Error running job:', error);
      showNotification('Error', error.message || 'Failed to trigger job execution');
    }
  };

  // Job columns for DataGrid
  const jobColumns = [
    { field: 'JOB_NAME', headerName: 'Job Name', flex: 1 },
    { 
      field: 'DOMAIN_ID', 
      headerName: 'Domain', 
      flex: 1,
      renderCell: (params) => {
        if (!params || !params.row) {
          return 'No Row Data';
        }
        
        if (!params.row.DOMAIN_ID) {
          return 'No Domain Selected';
        }
        
        if (!domains || domains.length === 0) {
          return 'No Domains Available';
        }
        
        // Convert the UUIDs to lowercase for consistent matching
        const jobDomainId = String(params.row.DOMAIN_ID).toLowerCase();
        
        // Find domain with case-insensitive comparison
        const domain = domains.find(d => String(d.id).toLowerCase() === jobDomainId);
        
        return (
          <span>
            {domain ? domain.DOMAIN_NAME : `Domain ID: ${params.row.DOMAIN_ID}`}
          </span>
        );
      }
    },
    { 
      field: 'JOB_TYPE', 
      headerName: 'Job Type', 
      width: 180,
      renderCell: (params) => {
        const jobType = jobTypes.find(type => type.value === params.value) || { 
          value: params.value || 'access_termination', 
          label: params.value ? params.value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Access Termination'
        };
        
        let color = 'primary';
        switch(jobType.value) {
          case 'admin_group_scan': color = 'secondary'; break;
          case 'terminated_user_scan': color = 'error'; break;
          case 'patch_scan': color = 'info'; break;
          default: color = 'primary';
        }
        
        return (
          <mui.Chip 
            label={jobType.label}
            color={color}
            size="small"
            sx={{ maxWidth: '160px' }}
          />
        );
      }
    },
    { field: 'SCHEDULE_TYPE', headerName: 'Schedule Type', width: 120 },
    { 
      field: 'SCHEDULE_TIME', 
      headerName: 'Schedule Time', 
      width: 150,
      renderCell: (params) => {
        if (!params || !params.value) {
          return <span>No Time Set</span>;
        }
        
        // For time strings like "14:30:00" or "14:41:27"
        if (typeof params.value === 'string' && params.value.includes(':')) {
          return <span>{params.value}</span>;
        }
        
        // Any other format, return the value as a string
        return <span>{String(params.value)}</span>;
      }
    },
    { 
      field: 'ENABLED', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => {
        // Determine the status and styling
        let label = 'Unknown';
        let color = 'default';
        
        if (params.row.ENABLED === true) {
          label = 'Active';
          color = 'success';
        } else if (params.row.ENABLED === false) {
          label = 'Paused';
          color = 'warning';
        }
        
        return (
          <mui.Chip 
            label={label} 
            color={color} 
            size="small"
            sx={{ minWidth: '70px' }}
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      renderCell: (params) => (
        <mui.Stack direction="row" spacing={1}>
          <mui.Tooltip title={params.row.ENABLED ? "Disable Job" : "Enable Job"}>
            <mui.Switch
              size="small"
              checked={params.row.ENABLED}
              onChange={() => toggleJobStatus(params.row)}
              color="success"
            />
          </mui.Tooltip>
          <mui.Tooltip title="Run Now">
            <mui.IconButton 
              size="small" 
              color="primary"
              onClick={() => runJob(params.row)}
            >
              <PlayArrowIcon fontSize="small" />
            </mui.IconButton>
          </mui.Tooltip>
          {params.row.JOB_TYPE !== 'access_termination' && params.row.LAST_RUN_DATE && (
            <mui.Tooltip title="View Results">
              <mui.IconButton 
                size="small" 
                color="info"
                onClick={() => viewJobResults(params.row.id, params.row.JOB_TYPE)}
              >
                <AssessmentIcon fontSize="small" />
              </mui.IconButton>
            </mui.Tooltip>
          )}
          <mui.IconButton size="small" onClick={() => editJob(params.row)}>
            <EditIcon fontSize="small" />
          </mui.IconButton>
          <mui.IconButton size="small" onClick={() => deleteJob(params.row.id)}>
            <DeleteIcon fontSize="small" />
          </mui.IconButton>
        </mui.Stack>
      ),
    },
  ];

  return (
    <mui.Paper sx={{ p: 3, mb: 4 }}>
      <SectionHeader 
        title="Job Scheduling" 
        icon={<ScheduleIcon />} 
        description="Schedule automated network access termination jobs" 
        action={
          <mui.Stack direction="row" spacing={2}>
            {jobs.length > 0 && (
              <>
                <mui.Button 
                  variant="outlined" 
                  color="warning"
                  startIcon={<PauseCircleOutlineIcon />}
                  onClick={() => bulkUpdateJobStatus(false)}
                  disabled={showJobForm || jobs.every(job => !job.ENABLED)}
                >
                  Pause All Jobs
                </mui.Button>
                <mui.Button 
                  variant="outlined" 
                  color="success"
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={() => bulkUpdateJobStatus(true)}
                  disabled={showJobForm || jobs.every(job => job.ENABLED)}
                >
                  Enable All Jobs
                </mui.Button>
              </>
            )}
            <mui.Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setShowJobForm(true)}
              disabled={showJobForm || domains.length === 0}
            >
              Add Job
            </mui.Button>
          </mui.Stack>
        }
      />
      
      {domains.length === 0 && (
        <mui.Alert severity="warning" sx={{ mb: 3 }}>
          <mui.AlertTitle>No Domains Available</mui.AlertTitle>
          You need to configure at least one AD domain before creating jobs.
          <mui.Box sx={{ mt: 1 }}>
            <mui.Button 
              size="small" 
              variant="outlined" 
              onClick={() => changeView('domains', 'Domain Management')}
            >
              Configure Domains
            </mui.Button>
          </mui.Box>
        </mui.Alert>
      )}
      
      {showJobForm && (
        <mui.Box sx={{ mt: 3, mb: 4, p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
          <mui.Typography variant="h6" gutterBottom>
            {editJobId ? 'Edit Job Schedule' : 'Add New Job Schedule'}
          </mui.Typography>

          <mui.Box component="form" onSubmit={addJob}>
            <mui.Grid container spacing={3}>
              <mui.Grid item xs={12} md={6}>
                <NormalTextField
                  label="Job Name"
                  name="JOB_NAME"
                  value={newJob.JOB_NAME}
                  onChange={handleJobChange}
                  required={true}
                  helperText="Name for this scheduled job"
                />
              </mui.Grid>
              <mui.Grid item xs={12} md={6}>
                <mui.FormControl fullWidth margin="normal">
                  <mui.InputLabel id="domain-select-label">Domain</mui.InputLabel>
                  <mui.Select
                    labelId="domain-select-label"
                    id="domain-select"
                    name="DOMAIN_ID"
                    value={newJob.DOMAIN_ID}
                    label="Domain"
                    onChange={handleJobChange}
                    required
                  >
                    {domains.map((domain) => (
                      <mui.MenuItem key={domain.id} value={domain.id}>
                        {domain.DOMAIN_NAME}
                      </mui.MenuItem>
                    ))}
                  </mui.Select>
                  <mui.FormHelperText>AD Domain to use for this job</mui.FormHelperText>
                </mui.FormControl>
              </mui.Grid>
              <mui.Grid item xs={12} md={6}>
                <mui.FormControl fullWidth margin="normal">
                  <mui.InputLabel id="job-type-label">Job Type</mui.InputLabel>
                  <mui.Select
                    labelId="job-type-label"
                    id="job-type-select"
                    name="JOB_TYPE"
                    value={newJob.JOB_TYPE}
                    label="Job Type"
                    onChange={handleJobChange}
                    required
                  >
                    {jobTypes.map((type) => (
                      <mui.MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </mui.MenuItem>
                    ))}
                  </mui.Select>
                  <mui.FormHelperText>
                    {jobTypes.find(type => type.value === newJob.JOB_TYPE)?.description || "Type of job to run"}
                  </mui.FormHelperText>
                </mui.FormControl>
              </mui.Grid>
              <mui.Grid item xs={12} md={6}>
                <mui.FormControl fullWidth margin="normal">
                  <mui.InputLabel id="schedule-type-label">Schedule Type</mui.InputLabel>
                  <mui.Select
                    labelId="schedule-type-label"
                    id="schedule-type"
                    name="SCHEDULE_TYPE"
                    value={newJob.SCHEDULE_TYPE}
                    label="Schedule Type"
                    onChange={handleJobChange}
                  >
                    <mui.MenuItem value="daily">Daily</mui.MenuItem>
                    <mui.MenuItem value="weekly">Weekly</mui.MenuItem>
                  </mui.Select>
                  <mui.FormHelperText>How often to run this job</mui.FormHelperText>
                </mui.FormControl>
              </mui.Grid>
              <mui.Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <mui.FormControl fullWidth margin="normal">
                    <TimePicker
                      label="Schedule Time"
                      value={newJob.SCHEDULE_TIME}
                      onChange={handleScheduleTimeChange}
                      ampm={false} // Use 24-hour format
                      renderInput={(params) => <mui.TextField {...params} />}
                    />
                    <mui.FormHelperText>Time to run the job (24-hour format)</mui.FormHelperText>
                  </mui.FormControl>
                </LocalizationProvider>
              </mui.Grid>
              {newJob.SCHEDULE_TYPE === 'weekly' && (
                <mui.Grid item xs={12}>
                  <mui.FormControl fullWidth margin="normal">
                    <mui.InputLabel id="days-of-week-label">Days of Week</mui.InputLabel>
                    <mui.Select
                      labelId="days-of-week-label"
                      id="days-of-week"
                      multiple
                      value={newJob.DAYS_OF_WEEK}
                      onChange={handleDaysOfWeekChange}
                      input={<mui.OutlinedInput label="Days of Week" />}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                        <mui.MenuItem key={day} value={day}>
                          <mui.Checkbox checked={newJob.DAYS_OF_WEEK.indexOf(day) > -1} />
                          <mui.ListItemText primary={day} />
                        </mui.MenuItem>
                      ))}
                    </mui.Select>
                    <mui.FormHelperText>Days to run the job</mui.FormHelperText>
                  </mui.FormControl>
                </mui.Grid>
              )}
              <mui.Grid item xs={12}>
                <mui.FormControlLabel
                  control={
                    <mui.Switch
                      checked={newJob.ENABLED}
                      onChange={(e) => setNewJob({ ...newJob, ENABLED: e.target.checked })}
                      name="ENABLED"
                      color="primary"
                    />
                  }
                  label="Enabled"
                />
              </mui.Grid>
              <mui.Grid item xs={12}>
                <mui.Stack direction="row" spacing={2}>
                  <mui.Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    startIcon={editJobId ? <CloudSyncIcon /> : <AddIcon />}
                  >
                    {editJobId ? 'Update Job' : 'Add Job'}
                  </mui.Button>
                  <mui.Button 
                    onClick={resetJobForm} 
                    variant="outlined" 
                    color="secondary"
                  >
                    Cancel
                  </mui.Button>
                </mui.Stack>
              </mui.Grid>
            </mui.Grid>
          </mui.Box>
        </mui.Box>
      )}
      
      <mui.Box sx={{ height: 400, width: '100%', mt: showJobForm ? 0 : 3 }}>
        {jobs.length > 0 ? (
          <DataGrid
            rows={jobs.filter(job => job && job.id)}
            columns={jobColumns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            disableSelectionOnClick
            getRowId={(row) => String(row.id || 'unknown')}
            components={{
              NoRowsOverlay: () => (
                <mui.Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <mui.Typography color="text.secondary">No jobs found</mui.Typography>
                </mui.Box>
              )
            }}
          />
        ) : (
          <mui.Box sx={{ p: 3, textAlign: 'center' }}>
            <ScheduleIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <mui.Typography variant="body1" gutterBottom>
              No jobs scheduled
            </mui.Typography>
            <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add jobs to schedule automated termination of network access
            </mui.Typography>
            {domains.length > 0 && (
              <mui.Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setShowJobForm(true)}
              >
                Add Job
              </mui.Button>
            )}
          </mui.Box>
        )}
      </mui.Box>
    </mui.Paper>
  );
};

export default JobScheduling; 